import { __rest, __assign, __spreadArrays } from '../../_virtual/_tslib.js';
import React from 'react';
import classnames from 'classnames';
import Imgix, { Source as Source$1, Picture as Picture$1 } from 'react-imgix';
import hash from 'object-hash';
import isNode from 'in-node';
import breakpoints from '../../breakpoints.css.js';
import styles from './styles.css.js';

function addDefaultImgixParams(params) {
  return Object.assign({
    auto: ['compress', 'format'],
    ch: ['Width', 'DPR', 'Save-Data'] // Default to setting all client-hints (https://docs.imgix.com/apis/url/format/ch)

  }, params);
}

var Image = function Image(props) {
  var imgixParams = props.imgixParams,
      className = props.className,
      _a = props.lazyLoad,
      lazyLoad = _a === void 0 ? true : _a,
      defaultHtmlAttributes = props.htmlAttributes,
      id = props.id,
      rest = __rest(props, ["imgixParams", "className", "lazyLoad", "htmlAttributes", "id"]);

  var htmlAttributes = __assign({
    id: id
  }, defaultHtmlAttributes);

  var sizes = []; // Order matters

  var responsiveAreasPropNames = ['sizeXxlg', 'sizeXlg', 'sizeLg', 'sizeMd', 'sizeSm', 'sizeXs'];
  responsiveAreasPropNames.forEach(function (propName) {
    var breakpointName = propName.slice(4).toLowerCase() || null; // "SizeXs" => "xs"

    var mediaQuery = breakpoints["breakpoint-" + breakpointName] || undefined; // "xs"  => "360px"

    if (propName in props) {
      // @ts-ignore TS can't tell this prop exists
      sizes.push(mediaQuery + " " + props[propName]);
    }
  });

  if (props.size) {
    sizes.push(props.size);
  }

  var formattedSizes = sizes.join(', ');
  var imgixParamsWithDefault = addDefaultImgixParams(imgixParams); // When lazy-loading is enabled, LazySizes directly mutates the DOM node, so we do not allow
  //  React to overwrite attributes/props set by that library (src, srcset, sizes, and className).
  //  This is important for render "purity", especially when hydrating a SSR response.
  //
  // For example: Images are eagerly loaded as soon as the initial HTML is parsed (LazySizes
  //  is initialized outside of the React render pipeline). Therefore, it's possible for images
  //  to already be loaded before the corresponding <Image> tag is hydrated/rendered on the client
  //  side for the first time. LazySizes will change the classnames independently of React, so by
  //  the time `React.hydrate` is called the document HTML may have been mutated. In that case, we
  //  need to set the appropriate className (`lazyloading` or `lazyloaded` instead of `lazyload`)
  //  in order to match the DOM. In general, we ensure certain classes matches those currently set
  //  in the DOM since that state is only partially managed by React.

  var lazySizesClassNames = ['ls-is-cached', 'lazyloading', 'lazyloaded', 'lazypreload', 'lazyerror', 'lazyautosizes'];
  var existingLazySizesClasses = []; // We generate a unique identifier based on the props, in order to find this element in the DOM
  //  during client-side rehydration. There are edge-cases where this won't work (multiple images
  //  with identical props), so we use a className as a "best-effort". In order to generate truly
  //  unique IDs we could theoretically hash the path to the React component from the root node.
  //  That data is not currently exposed by React.
  //
  // See: https://github.com/facebook/react/issues/15435

  var hashedPropsClassName = id || "image-" + hash(props).slice(0, 8);

  if (lazyLoad && !isNode) {
    // Get the DOM node of an element that matches this component's props exactly. It may not be
    //  the same element as we're actively rendering now: there's an edge case where the same image
    //  is rendered twice, once below-the-fold and once above-the-fold, in which case they will
    //  have different LazySizes classnames and attributes. In that scenario we prefer the image that
    //  is the "most loaded". The markup rendered by React will not match the markup in the DOM, so
    //  we have to suppress the warnings from React hydration.
    var loadingStateClassNames_1 = ['lazyload', 'lazypreload', 'lazyloading', 'lazyloaded'];
    var matchingDomNodes = Array.from(document.getElementsByClassName(hashedPropsClassName));
    var mostLoadedDomNode = matchingDomNodes.sort(function (a, b) {
      var stateOfImageA = loadingStateClassNames_1.findIndex(function (className) {
        return a.classList.contains(className);
      });
      var stateOfImageB = loadingStateClassNames_1.findIndex(function (className) {
        return b.classList.contains(className);
      });
      return stateOfImageB - stateOfImageA;
    })[0];

    if (mostLoadedDomNode) {
      existingLazySizesClasses = Array.from(mostLoadedDomNode.classList).filter(function (className) {
        return lazySizesClassNames.indexOf(className) !== -1;
      }); // Allow LazySizes to manage `src`, `srcset`, and `sizes` itself.
      //  We set those attributes as `data-` attributes instead. LazySizes will manage converting
      //  those to the corresponding DOM attributes for us, so we always prefer the existing DOM
      //  attributes.

      htmlAttributes = __assign({
        src: mostLoadedDomNode.getAttribute('src') || undefined,
        srcSet: mostLoadedDomNode.getAttribute('srcset') || undefined,
        sizes: mostLoadedDomNode.getAttribute('sizes') || undefined,
        suppressHydrationWarning: matchingDomNodes.length > 1
      }, htmlAttributes);
    }
  }

  var classNames = classnames.apply(void 0, __spreadArrays([className, hashedPropsClassName, styles.hideAltTextWhileLoading, {
    lazyload: lazyLoad && existingLazySizesClasses.length === 0
  }], existingLazySizesClasses));
  return React.createElement(Imgix, __assign({
    htmlAttributes: htmlAttributes,
    sizes: formattedSizes || undefined,
    className: classNames,
    attributeConfig: lazyLoad ? {
      src: 'data-src',
      srcSet: 'data-srcset',
      sizes: 'data-sizes'
    } : undefined,
    imgixParams: imgixParamsWithDefault
  }, rest));
};

var Source = function Source(_a) {
  var imgixParams = _a.imgixParams,
      props = __rest(_a, ["imgixParams"]);

  imgixParams = addDefaultImgixParams(imgixParams);
  return React.createElement(Source$1, __assign({
    imgixParams: imgixParams
  }, props));
};

var Picture = function Picture(props) {
  return React.createElement(Picture$1, __assign({}, props));
}; // Exports

export default Image;
export { Picture, Source };
//# sourceMappingURL=index.js.map
